import { Card, FilledInput, FormControl, IconButton, InputAdornment, InputLabel } from '@mui/material';
import { useContext, useRef, useState } from 'react';
import SendIcon from 'remixicon-react/SendPlane2FillIcon';
import { useMessageSender } from '../../../../Utilities/SendMessage';
import { AppContext } from '../../../App/AppContext';
import { INLINE_COMPOSER_LOCKED_LABEL } from '../../../ConversationalForms/constants';
import { useConversationalForms } from '../../../ConversationalForms/FormsContext';
import { useLiveInlineField } from '../../../ConversationalForms/useLiveInlineField';
import styles from './MessageSender.module.css';
function MessageSender() {

  const [sending, setSending] = useState(false);
  const sendMessage = useMessageSender();
  const inputRef = useRef<HTMLInputElement>(null);
  const { dispatch } = useContext(AppContext);
  // Deliberate divergence from the rest of the widget (design reference,
  // "Deliberate divergences from production"): while conversational forms are
  // active the composer never locks, because the parse-latency spec depends on
  // the student being able to keep typing during an in-flight turn.
  const { active: formsActive, sendTurn } = useConversationalForms();
  const locked = sending && !formsActive;
  // The one exception: while an inline sensitive control is live, the composer
  // is really disabled and the visitor continues only through the control's
  // Save or Skip. Half-typed text is kept and comes back when it unlocks.
  const liveInline = useLiveInlineField();
  const inlineLocked = formsActive && liveInline !== undefined;

  const [inputMessage, setInputMessage] = useState('');

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputMessage(event.target.value);
  };

  // Task 4.6: persist keystroke activity so the proactive idle timer resets on typing
  const handleKeyDown = () => {
    dispatch({ type: 'setProactiveLastActivityAt', timestamp: Date.now() });
  };

  const onKeyUp = async (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && inputMessage !== "") {
      await onSend();
    }
  }

  const onSend = async () => {
    if (inputMessage !== "") {
      let newQuestion = inputMessage
      setSending(true);
      setInputMessage("");
      if (formsActive) {
        // The conversational-forms turn owns its own latency, failure, and
        // receipt handling, and queues FIFO behind any in-flight turn.
        await sendTurn(newQuestion)
      } else {
        await sendMessage({ content: newQuestion, direction: 0, time: new Date(Date.now()) })
      }
      setSending(false);
      // Re-enable then refocus so the user can immediately type the next message.
      // Must be deferred one tick so React re-renders the enabled input first.
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }

  return (

    <Card sx={{ p: 0 }} className={styles.messageSender} >
      <FormControl variant="filled" disabled={locked || inlineLocked}>
        <InputLabel htmlFor="send-message-input" id="send-message-helper-text">
          {inlineLocked ? INLINE_COMPOSER_LOCKED_LABEL : 'Send a Message'}
        </InputLabel>
        <FilledInput endAdornment={
          <InputAdornment position="end">
            <IconButton
              onClick={onSend}
              aria-label="Send message"
              edge="end"
              disabled={locked || inlineLocked || inputMessage === ''}
            >
              <SendIcon className={(sending ? styles.messageSenderIconSending : "")} />
            </IconButton>
          </InputAdornment>
        }
          onKeyUp={onKeyUp}
          onKeyDown={handleKeyDown}
          value={inputMessage}
          onChange={handleChange}
          inputRef={inputRef}
          disableUnderline={true} id="send-message-input" aria-describedby="send-message-helper-text" />
      </FormControl>

    </Card>
  );
}
export default MessageSender;
